import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCart } from '../context/CartContext';

const Checkout = () => {
  const { cart, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    // Customer Info
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    
    // Shipping Address
    address: '',
    city: '',
    postalCode: '',
    country: 'Singapore',
    
    // Shipping Method
    shippingMethod: 'standard',
    
    // Payment
    paymentMethod: 'card',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: ''
  });

  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  const formatPrice = (price) => {
    // 如果价格已经是格式化的字符串（如 "S$22.90"），直接返回
    if (typeof price === 'string') {
      return price;
    }
    // 如果价格是数字，格式化为 "S$" 格式
    return `S$${price.toFixed(2)}`;
  };

  const shippingOptions = [
    { id: 'standard', name: 'Standard Delivery (3-5 business days)', price: 15, free: getCartTotal() > 200 },
    { id: 'express', name: 'Express Delivery (1-2 business days)', price: 25, free: false },
    { id: 'overnight', name: 'Next Day Delivery (24 hours)', price: 45, free: false }
  ];

  const getShippingCost = () => {
    const selectedShipping = shippingOptions.find(option => option.id === formData.shippingMethod);
    return selectedShipping?.free ? 0 : selectedShipping?.price || 0;
  };

  const totalAmount = getCartTotal() + getShippingCost();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateStep = (stepNumber) => {
    const newErrors = {};
    
    switch (stepNumber) {
      case 1:
        if (!formData.firstName) newErrors.firstName = 'Please enter your first name';
        if (!formData.lastName) newErrors.lastName = 'Please enter your last name';
        if (!formData.email) newErrors.email = 'Please enter your email';
        if (!formData.phone) newErrors.phone = 'Please enter your phone number';
        break;
      
      case 2:
        if (!formData.address) newErrors.address = 'Please enter your address';
        if (!formData.city) newErrors.city = 'Please enter your city';
        if (!formData.postalCode) newErrors.postalCode = 'Please enter your postal code';
        break;
      
      case 4:
        if (formData.paymentMethod === 'card') {
          if (!formData.cardNumber) newErrors.cardNumber = 'Please enter card number';
          if (!formData.expiryDate) newErrors.expiryDate = 'Please enter expiry date';
          if (!formData.cvv) newErrors.cvv = 'Please enter CVV';
          if (!formData.cardholderName) newErrors.cardholderName = 'Please enter cardholder name';
        }
        break;
      
      default:
        // 其他步骤不需要验证
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handlePrevStep = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(4)) return;
    
    setIsProcessing(true);
    
    // Simulate payment processing
    setTimeout(() => {
      clearCart();
      navigate('/order-complete', { 
        state: { 
          orderNumber: `GT-${Date.now()}`,
          orderData: { ...formData, total: totalAmount }
        }
      });
    }, 3000);
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-dark-400 pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-16">
            <div className="text-8xl mb-6">🛒</div>
            <h2 className="text-3xl font-bold text-white mb-4">购物车是空的</h2>
            <p className="text-xl text-gray-400 mb-8">
              请先添加商品到购物车再进行结账
            </p>
            <Link to="/shop">
              <button className="btn-primary text-lg px-8 py-4">
                开始购物
              </button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-400 pt-24 pb-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-futuristic font-bold mb-4">
            <span className="gradient-text">CHECKOUT</span>
          </h1>
          <p className="text-xl text-gray-400">
            Secure and fast checkout process
          </p>
        </motion.div>

        {/* Progress Bar */}
        <motion.div
          className="mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="flex items-center justify-center space-x-4">
            {[
              { number: 1, title: 'Customer Info' },
              { number: 2, title: 'Shipping Address' },
              { number: 3, title: 'Shipping Method' },
              { number: 4, title: 'Payment Info' }
            ].map((stepInfo, index) => (
              <div key={stepInfo.number} className="flex items-center">
                <div className={`flex items-center space-x-2 ${
                  step >= stepInfo.number ? 'text-primary-400' : 'text-gray-400'
                }`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                    step >= stepInfo.number 
                      ? 'bg-primary-500 text-white' 
                      : 'bg-gray-600 text-gray-400'
                  }`}>
                    {step > stepInfo.number ? '✓' : stepInfo.number}
                  </div>
                  <span className="text-sm font-medium hidden sm:block">{stepInfo.title}</span>
                </div>
                {index < 3 && (
                  <div className={`w-8 h-0.5 mx-2 ${
                    step > stepInfo.number ? 'bg-primary-500' : 'bg-gray-600'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit}>
              {/* Step 1: Customer Information */}
              {step === 1 && (
                <motion.div
                  className="card-glass p-8 rounded-lg"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2 className="text-2xl font-semibold text-white mb-6">Customer Information</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 bg-dark-300 border rounded-lg text-white ${
                          errors.firstName ? 'border-red-500' : 'border-white/20'
                        } focus:outline-none focus:border-primary-500`}
                        placeholder="Enter your first name"
                      />
                      {errors.firstName && (
                        <p className="text-red-400 text-sm mt-1">{errors.firstName}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 bg-dark-300 border rounded-lg text-white ${
                          errors.lastName ? 'border-red-500' : 'border-white/20'
                        } focus:outline-none focus:border-primary-500`}
                        placeholder="Enter your last name"
                      />
                      {errors.lastName && (
                        <p className="text-red-400 text-sm mt-1">{errors.lastName}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 bg-dark-300 border rounded-lg text-white ${
                          errors.email ? 'border-red-500' : 'border-white/20'
                        } focus:outline-none focus:border-primary-500`}
                        placeholder="your@email.com"
                      />
                      {errors.email && (
                        <p className="text-red-400 text-sm mt-1">{errors.email}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 bg-dark-300 border rounded-lg text-white ${
                          errors.phone ? 'border-red-500' : 'border-white/20'
                        } focus:outline-none focus:border-primary-500`}
                        placeholder="+65 1234 5678"
                      />
                      {errors.phone && (
                        <p className="text-red-400 text-sm mt-1">{errors.phone}</p>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Shipping Address */}
              {step === 2 && (
                <motion.div
                  className="card-glass p-8 rounded-lg"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2 className="text-2xl font-semibold text-white mb-6">Shipping Address</h2>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Detailed Address *
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 bg-dark-300 border rounded-lg text-white ${
                          errors.address ? 'border-red-500' : 'border-white/20'
                        } focus:outline-none focus:border-primary-500`}
                        placeholder="Street address"
                      />
                      {errors.address && (
                        <p className="text-red-400 text-sm mt-1">{errors.address}</p>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-white text-sm font-medium mb-2">
                          City *
                        </label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 bg-dark-300 border rounded-lg text-white ${
                            errors.city ? 'border-red-500' : 'border-white/20'
                          } focus:outline-none focus:border-primary-500`}
                          placeholder="Singapore"
                        />
                        {errors.city && (
                          <p className="text-red-400 text-sm mt-1">{errors.city}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-white text-sm font-medium mb-2">
                          Postal Code *
                        </label>
                        <input
                          type="text"
                          name="postalCode"
                          value={formData.postalCode}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 bg-dark-300 border rounded-lg text-white ${
                            errors.postalCode ? 'border-red-500' : 'border-white/20'
                          } focus:outline-none focus:border-primary-500`}
                          placeholder="123456"
                        />
                        {errors.postalCode && (
                          <p className="text-red-400 text-sm mt-1">{errors.postalCode}</p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-white text-sm font-medium mb-2">
                          Country *
                        </label>
                        <select
                          name="country"
                          value={formData.country}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-dark-300 border border-white/20 rounded-lg text-white focus:outline-none focus:border-primary-500"
                        >
                          <option value="Singapore">Singapore</option>
                          <option value="Malaysia">Malaysia</option>
                          <option value="Thailand">Thailand</option>
                          <option value="Indonesia">Indonesia</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 3: Shipping Method */}
              {step === 3 && (
                <motion.div
                  className="card-glass p-8 rounded-lg"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2 className="text-2xl font-semibold text-white mb-6">Shipping Method</h2>
                  
                  <div className="space-y-4">
                    {shippingOptions.map((option) => (
                      <label
                        key={option.id}
                        className={`block p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                          formData.shippingMethod === option.id
                            ? 'border-primary-500 bg-primary-500/10'
                            : 'border-white/20 hover:border-white/40'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <input
                            type="radio"
                            name="shippingMethod"
                            value={option.id}
                            checked={formData.shippingMethod === option.id}
                            onChange={handleInputChange}
                            className="text-primary-500"
                          />
                          <div className="flex-1">
                            <div className="flex justify-between items-center">
                              <span className="text-white font-medium">{option.name}</span>
                              <span className="text-white font-bold">
                                {option.free ? (
                                  <span className="text-green-400">FREE</span>
                                ) : (
                                  formatPrice(option.price)
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Step 4: Payment Information */}
              {step === 4 && (
                <motion.div
                  className="card-glass p-8 rounded-lg"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6 }}
                >
                  <h2 className="text-2xl font-semibold text-white mb-6">Payment Information</h2>
                  
                  {/* Payment Methods */}
                  <div className="mb-6">
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <label className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                        formData.paymentMethod === 'card'
                          ? 'border-primary-500 bg-primary-500/10'
                          : 'border-white/20 hover:border-white/40'
                      }`}>
                        <div className="flex items-center space-x-3">
                          <input
                            type="radio"
                            name="paymentMethod"
                            value="card"
                            checked={formData.paymentMethod === 'card'}
                            onChange={handleInputChange}
                          />
                          <span className="text-white">💳 Credit/Debit Card</span>
                        </div>
                      </label>
                      
                      <label className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                        formData.paymentMethod === 'paypal'
                          ? 'border-primary-500 bg-primary-500/10'
                          : 'border-white/20 hover:border-white/40'
                      }`}>
                        <div className="flex items-center space-x-3">
                          <input
                            type="radio"
                            name="paymentMethod"
                            value="paypal"
                            checked={formData.paymentMethod === 'paypal'}
                            onChange={handleInputChange}
                          />
                          <span className="text-white">💰 PayPal</span>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Card Details */}
                  {formData.paymentMethod === 'card' && (
                    <div className="space-y-6">
                      <div>
                        <label className="block text-white text-sm font-medium mb-2">
                          Card Number *
                        </label>
                        <input
                          type="text"
                          name="cardNumber"
                          value={formData.cardNumber}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 bg-dark-300 border rounded-lg text-white ${
                            errors.cardNumber ? 'border-red-500' : 'border-white/20'
                          } focus:outline-none focus:border-primary-500`}
                          placeholder="1234 5678 9012 3456"
                        />
                        {errors.cardNumber && (
                          <p className="text-red-400 text-sm mt-1">{errors.cardNumber}</p>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-6">
                        <div>
                                                  <label className="block text-white text-sm font-medium mb-2">
                          Expiry Date *
                        </label>
                          <input
                            type="text"
                            name="expiryDate"
                            value={formData.expiryDate}
                            onChange={handleInputChange}
                            className={`w-full px-4 py-3 bg-dark-300 border rounded-lg text-white ${
                              errors.expiryDate ? 'border-red-500' : 'border-white/20'
                            } focus:outline-none focus:border-primary-500`}
                            placeholder="MM/YY"
                          />
                          {errors.expiryDate && (
                            <p className="text-red-400 text-sm mt-1">{errors.expiryDate}</p>
                          )}
                        </div>
                        
                        <div>
                          <label className="block text-white text-sm font-medium mb-2">
                            CVV *
                          </label>
                          <input
                            type="text"
                            name="cvv"
                            value={formData.cvv}
                            onChange={handleInputChange}
                            className={`w-full px-4 py-3 bg-dark-300 border rounded-lg text-white ${
                              errors.cvv ? 'border-red-500' : 'border-white/20'
                            } focus:outline-none focus:border-primary-500`}
                            placeholder="123"
                          />
                          {errors.cvv && (
                            <p className="text-red-400 text-sm mt-1">{errors.cvv}</p>
                          )}
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-white text-sm font-medium mb-2">
                          Cardholder Name *
                        </label>
                        <input
                          type="text"
                          name="cardholderName"
                          value={formData.cardholderName}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 bg-dark-300 border rounded-lg text-white ${
                            errors.cardholderName ? 'border-red-500' : 'border-white/20'
                          } focus:outline-none focus:border-primary-500`}
                          placeholder="As shown on card"
                        />
                        {errors.cardholderName && (
                          <p className="text-red-400 text-sm mt-1">{errors.cardholderName}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* PayPal */}
                  {formData.paymentMethod === 'paypal' && (
                    <div className="text-center py-8">
                      <div className="text-6xl mb-4">💰</div>
                      <p className="text-gray-300">
                        After clicking "Complete Order", you will be redirected to PayPal to complete payment.
                      </p>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8">
                {step > 1 && (
                  <button
                    type="button"
                    onClick={handlePrevStep}
                    className="btn-outline"
                  >
                    ← Previous
                  </button>
                )}
                
                <div className="ml-auto">
                  {step < 4 ? (
                    <button
                      type="button"
                      onClick={handleNextStep}
                      className="btn-primary"
                    >
                      Next →
                    </button>
                  ) : (
                    <motion.button
                      type="submit"
                      disabled={isProcessing}
                      className={`btn-primary relative ${
                        isProcessing ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      whileHover={!isProcessing ? { scale: 1.05 } : {}}
                      whileTap={!isProcessing ? { scale: 0.95 } : {}}
                    >
                      {isProcessing ? (
                        <>
                          <span className="opacity-0">Complete Order</span>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span className="ml-2">Processing...</span>
                          </div>
                        </>
                      ) : (
                        'Complete Order'
                      )}
                    </motion.button>
                  )}
                </div>
              </div>
            </form>
          </div>

          {/* Order Summary */}
          <motion.div
            className="lg:col-span-1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="card-glass p-6 rounded-lg sticky top-24">
              <h3 className="text-xl font-semibold text-white mb-6">Order Summary</h3>
              
              {/* Cart Items */}
              <div className="space-y-3 mb-6">
                {cart.map((item) => (
                  <div key={item.id} className="flex items-center space-x-3">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-12 h-12 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <p className="text-white text-sm font-medium line-clamp-1">
                        {item.name}
                      </p>
                      <p className="text-gray-400 text-xs">
                        Qty: {item.quantity}
                      </p>
                    </div>
                    <span className="text-white font-medium">
                      {formatPrice(item.price * item.quantity)}
                    </span>
                  </div>
                ))}
              </div>
              
              <div className="border-t border-white/20 pt-4 space-y-3">
                <div className="flex justify-between text-gray-300">
                  <span>Subtotal:</span>
                  <span>{formatPrice(getCartTotal())}</span>
                </div>
                
                <div className="flex justify-between text-gray-300">
                  <span>Shipping:</span>
                  <span>
                    {getShippingCost() === 0 ? (
                                              <span className="text-green-400">FREE</span>
                    ) : (
                      formatPrice(getShippingCost())
                    )}
                  </span>
                </div>
                
                <div className="flex justify-between text-xl font-bold text-white border-t border-white/20 pt-3">
                  <span>Total:</span>
                  <span className="gradient-text">{formatPrice(totalAmount)}</span>
                </div>
              </div>
              
              {/* Security Info */}
              <div className="mt-6 p-4 bg-gradient-to-r from-green-500/10 to-green-600/10 rounded-lg border border-green-500/20">
                <div className="flex items-center space-x-2 mb-2">
                  <span>🔒</span>
                  <span className="text-green-400 font-medium text-sm">安全保障</span>
                </div>
                <p className="text-xs text-gray-300">
                  您的支付信息采用256位SSL加密保护，与银行级别安全标准相同。
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
